import './App.css';

import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import {
  Header,
  HowToPlayModal,
  Keyboard,
  MilestoneCelebration,
  Puzzles,
  EndScreen,
} from './components';
import { checkValidity } from './util/checkValidity';
import { generateWordlist } from './util/generateWordlist';
import { sortByValue } from './util/sortByValue';

const Container = styled.div`
  display: flex;
  justify-content: center;
  background-color: var(--page-bg);
`;

const Content = styled.div`
  max-width: 600px;
  box-shadow: 4px 4px 40px 4px var(--shadow);
  background-color: var(--surface);
  margin-bottom: 200px;
`;

const milestones = [10, 30, 50, 100, 200, 500, 900, 1000];

function App() {
  const totalWords = 1000;
  const [wordlist, setWordlist] = useState(generateWordlist(totalWords));
  const [guesslist, setGuesslist] = useState<string[]>([]);
  const [progressHistory, setProgressHistory] = useState<number[]>([]);
  const [invalidWordMessage, setInvalidWordMessage] = useState('');
  const [celebrationMilestone, setCelebrationMilestone] = useState<number | null>(
    null
  );
  const [working, setWorking] = useState('');
  const celebratedMilestones = useRef<Set<number>>(new Set());
  const [showTutorial, setShowTutorial] = useState(() => {
    try {
      const isMobileViewport = window.matchMedia('(max-width: 760px)').matches;
      if (isMobileViewport) return false;

      return localStorage.getItem('kilordle-how-to-play-seen') !== 'true';
    } catch {
      return true;
    }
  });

  const maxGuesses = 1005;
  const expired = guesslist.length >= maxGuesses;

  const addKey = useCallback((key: string) => {
    if (key === '-' || key === 'Backspace')
      setWorking((tmp) => tmp.slice(0, tmp.length - 1));
    else if (
      (key === '+' || key === 'Enter') &&
      working.length === 5
    ) {
      if (!checkValidity(working)) {
        setInvalidWordMessage('Not in word list');
        return;
      }

      // lets go.
      const newGuesslist = guesslist.concat([working]);
      const newWordlist = sortByValue(wordlist, newGuesslist);
      const clearedWords = totalWords - newWordlist.length;
      const reachedMilestone = milestones
        .filter(
          (milestone) =>
            clearedWords >= milestone && !celebratedMilestones.current.has(milestone)
        )
        .at(-1);

      if (reachedMilestone !== undefined) {
        milestones
          .filter((milestone) => milestone <= reachedMilestone)
          .forEach((milestone) => celebratedMilestones.current.add(milestone));
        setCelebrationMilestone(reachedMilestone);
      }

      setGuesslist(newGuesslist);
      setInvalidWordMessage('');
      setWorking('');
      setWordlist(newWordlist);
      setProgressHistory(
        progressHistory.concat([totalWords - (wordlist.length - 1)])
      );
    } else if (working.length !== 5 && key.length === 1 && key !== ' ') {
      setWorking((tmp) => tmp + key.toLowerCase());
    }
  }, [guesslist, progressHistory, wordlist, working]);

  useEffect(() => {
    if (!invalidWordMessage) return;

    const timeout = window.setTimeout(() => {
      setInvalidWordMessage('');
    }, 1600);

    return () => window.clearTimeout(timeout);
  }, [invalidWordMessage]);

  useEffect(() => {
    setWordlist((currentWordlist) => sortByValue(currentWordlist, []));
  }, []);

  useEffect(() => {
    function keyEvent(ev: KeyboardEvent) {
      addKey(ev.key);
    }
    if (!expired && !showTutorial) {
      window.addEventListener('keydown', keyEvent);
      if (working.length > 5) setWorking((tmp) => tmp.slice(0, 5));
      return () => window.removeEventListener('keydown', keyEvent);
    }
  }, [addKey, expired, showTutorial, working]);

  function closeTutorial() {
    try {
      localStorage.setItem('kilordle-how-to-play-seen', 'true');
    } catch { }
    setShowTutorial(false);
  }

  function getUsedLetters() {
    var letters: string[] = [];
    guesslist.forEach((word) => {
      for (var i = 0; i < word.length; i++) {
        var char = word.charAt(i);
        if (letters.indexOf(char) === -1) letters.push(char);
      }
    });
    return letters;
  }

  // const endScreenDemo = true;
  // useEffect(() => {
  //   if (endScreenDemo) {
  //     let fakeProgressHistory = [];
  //     let progress = 0;
  //     while (progress < 1000) {
  //       progress += Math.floor(Math.random() * 10);
  //       fakeProgressHistory.push(progress);
  //     }
  //     setProgressHistory(fakeProgressHistory);
  //     setWordlist([]);
  //     setGuesslist(Array(fakeProgressHistory.length).fill('placeholder'));
  //   }
  // }, []);

  return (
    <div className="App">
      {showTutorial ? <HowToPlayModal onClose={closeTutorial} /> : null}
      <MilestoneCelebration milestone={celebrationMilestone} />
      {invalidWordMessage ? (
        <div aria-live="polite" className="invalid-word-toast" role="status">
          {invalidWordMessage}
        </div>
      ) : null}
      <Container>
        <Content>
          <Header
            guesses={guesslist.length}
            limit={maxGuesses}
            remaining={wordlist.length}
          />
          <Puzzles
            expired={expired}
            guesslist={guesslist}
            wordlist={wordlist}
            working={working}
          />
          {wordlist.length === 0 ? (
            <EndScreen progressHistory={progressHistory} />
          ) : null}
          <Keyboard
            expired={expired}
            onKeyPress={(key) => addKey(key)}
            spotlight={showTutorial}
            usedLetters={getUsedLetters()}
          />
        </Content>
      </Container>
    </div>
  );
}

export default App;
